"use client";

import Link from "next/link";

import type { ReactNode } from "react";

import { ETICHETTA_ACCESA, FUOCO_HOME } from "@/components/home/stile";
import { CONSERVAZIONE, EMAIL_TITOLARE, TITOLARE } from "@/lib/legale";
import { MessaggioErrore, TONO_ERRORE } from "@/components/ui/MessaggioErrore";
import { GRUPPO, LEGENDA } from "@/components/moduli/stile";

/**
 * Il riquadro dell'informativa e delle due caselle, condiviso dai moduli
 * del sito: quello di /contatti e il briefing di /accoglienza.
 *
 * È condiviso e non copiato per una ragione che non è di comodità: il
 * testo dell'art. 13 e la formula delle due caselle sono il punto in cui
 * il sito dichiara che cosa fa dei dati, e due copie divergono al primo
 * ritocco. Un modulo che chiede la presa visione di un'informativa
 * scritta in modo diverso dall'altro è un modulo che non si sa più che
 * cosa abbia fatto dichiarare.
 *
 * Le caselle sono **due**, e non una, perché rispondono a due obblighi
 * diversi:
 *
 * - la presa visione è obbligatoria e non è un consenso. La base
 *   giuridica della risposta a chi scrive è l'esecuzione di misure
 *   precontrattuali (art. 6, par. 1, lett. b): scrivere «acconsento»
 *   lascerebbe credere che revocando si fermi il trattamento;
 * - il consenso agli aggiornamenti editoriali è facoltativo, separato,
 *   parte spento ed è l'unico dei due che si revoca.
 *
 * Nessuna delle due parte spuntata: una casella già segnata non è la
 * dichiarazione di chi compila, è una riga scritta al posto suo.
 */
export function InformativaConsensi({
  dati,
  presaVisione,
  onPresaVisione,
  consensoAggiornamenti,
  onConsensoAggiornamenti,
  errorePresaVisione,
  disabled = false,
  variante = "riquadro",
  testa,
  className = "",
}: {
  /** L'elenco dei dati che **quel** modulo raccoglie, per esteso. Cambia
   *  fra i due (il briefing chiede anche ruolo, partita IVA, settore e
   *  telefono) e l'art. 13 vuole che sia detto quello vero, non una
   *  formula che vale per tutti. */
  dati: string;
  presaVisione: boolean;
  onPresaVisione: (v: boolean) => void;
  consensoAggiornamenti: boolean;
  onConsensoAggiornamenti: (v: boolean) => void;
  errorePresaVisione?: string;
  /** Durante l'invio. È il `disabled` nativo del `<fieldset>`, che spegne
   *  da solo tutto quello che contiene. */
  disabled?: boolean;
  /** `riquadro` è la forma di sempre, il gruppo con il bordo dei moduli
   *  (`GRUPPO`), ed è quella del briefing di /accoglienza. `pannello` è la
   *  forma di /contatti dal 19/10/2026 (`uploads/contatti.html`, la colonna
   *  sinistra di «Prima di inviare»): l'occhiello rame del modello come
   *  legenda, il titolo che la pagina passa in `testa`, il testo Geist 15
   *  e le due caselle native (`.ct-spunta`). Testo, caselle e loro senso
   *  non cambiano: cambia solo la veste. Sostituisce la variante `piano`
   *  del 01/10/2026, che aveva solo /contatti come cliente. */
  variante?: "riquadro" | "pannello";
  /** Solo `pannello`: il titolo che sta fra la legenda e il testo
   *  («Prima di inviare.»), con l'`id` che la sezione intorno usa come
   *  nome. */
  testa?: ReactNode;
  className?: string;
}) {
  if (variante === "pannello") {
    return (
      <Pannello
        dati={dati}
        presaVisione={presaVisione}
        onPresaVisione={onPresaVisione}
        consensoAggiornamenti={consensoAggiornamenti}
        onConsensoAggiornamenti={onConsensoAggiornamenti}
        errorePresaVisione={errorePresaVisione}
        disabled={disabled}
        testa={testa}
        className={className}
      />
    );
  }
  return (
    /* L'art. 13 va reso **prima** della raccolta, cioè qui e non solo in
       una pagina che nessuno apre: chi compila un modulo deve sapere a
       chi sta dando i suoi dati mentre glieli sta dando. Il riquadro è
       volutamente l'unico senza tono, senza filigrana e senza numero di
       passaggio: non è un passaggio da compilare, è il testo che vale, e
       gli step colorati non devono sembrare più importanti di lui. */
    <fieldset
      disabled={disabled}
      className={`${GRUPPO} ${className}`}
    >
      {/* Stessa legenda dei passaggi dei due moduli, ma in `text-muted`:
          non è un passaggio da compilare e non porta numero. */}
      <legend className={`${LEGENDA} text-muted`}>Informativa e consensi</legend>

      <TestoInformativa
        dati={dati}
        className="m-0 max-w-[72ch] text-meta leading-[1.6] text-muted"
        classeLink="text-accent"
      />

      <div className="mt-4 flex flex-col gap-3">
        <Casella
          campo="presaVisione"
          checked={presaVisione}
          onChange={onPresaVisione}
          errore={errorePresaVisione}
          nota={NOTA_PRESA_VISIONE}
        >
          {/* «Ho letto», non «acconsento»: la ragione per esteso sta in
              `lib/contatti.ts`, accanto al controllo. */}
          Ho letto l&apos;informativa privacy e so come saranno trattati i miei
          dati.
        </Casella>

        <Casella
          campo="consensoAggiornamenti"
          checked={consensoAggiornamenti}
          onChange={onConsensoAggiornamenti}
          nota={NOTA_AGGIORNAMENTI}
        >
          Voglio ricevere gli aggiornamenti editoriali dell&apos;Osservatorio.
        </Casella>
      </div>
    </fieldset>
  );
}

/** Le due note sotto le caselle: le stesse parole nelle due vesti. */
const NOTA_PRESA_VISIONE =
  "Obbligatorio: senza questa conferma la richiesta non può essere presa in carico.";
const NOTA_AGGIORNAMENTI = `Facoltativo, e indipendente dalla risposta: si revoca in qualunque momento con una riga a ${EMAIL_TITOLARE}.`;

/**
 * Il testo dell'art. 13 reso prima della raccolta. Uno solo per le due
 * vesti: cambia il colore, non una parola.
 */
function TestoInformativa({
  dati,
  className,
  classeLink,
}: {
  dati: string;
  className: string;
  /** Il colore dei due collegamenti: l'accento del sito nel riquadro del
   *  briefing, il mare nel pannello di /contatti. */
  classeLink: string;
}) {
  return (
    <p className={className}>
      I dati che invii ({dati}) sono trattati da {TITOLARE} per rispondere alla
      richiesta e valutare un eventuale rapporto professionale. Non sono
      ceduti a terzi, non alimentano profilazione e una richiesta senza
      seguito viene cancellata entro {CONSERVAZIONE.parole}. Puoi chiedere in ogni
      momento accesso, rettifica o cancellazione scrivendo a{" "}
      <a
        href={`mailto:${EMAIL_TITOLARE}`}
        className={`${classeLink} underline-offset-2 hover:underline`}
      >
        {EMAIL_TITOLARE}
      </a>
      . Il testo completo, con basi giuridiche e tempi, è nell&apos;
      <Link
        href="/privacy"
        // Si apre in una scheda nuova di proposito: leggere
        // l'informativa non deve costare il modulo compilato fin qui.
        // Il `rel` serve anche su un link interno, perché
        // `target="_blank"` dà comunque alla pagina aperta un
        // riferimento a questa.
        target="_blank"
        rel="noopener noreferrer"
        // Sottolineato sempre, non solo al passaggio del mouse: è il
        // collegamento che rende l'informativa, e chi lo cerca deve
        // trovarlo senza passarci sopra.
        className={`${classeLink} underline underline-offset-2`}
      >
        informativa privacy
      </Link>
      .
    </p>
  );
}

/**
 * La veste di /contatti (19/10/2026): la colonna sinistra di «Prima di
 * inviare» del modello. Legenda = l'occhiello rame «Informativa e consensi
 * · Modalità riservata» (`.oi-k.acc`), poi il titolo della pagina, il testo
 * Geist 15 sonda (`.m-cta .sub`) e le due caselle (`.ct-spunta`).
 *
 * La legenda è `float` di proposito, come `.ct-p legend` nel modello: una
 * `<legend>` non flottante è disegnata dal browser **dentro il bordo** del
 * fieldset, fuori dal flusso del contenuto; flottante torna un blocco come
 * gli altri, e il primo figlio dopo di lei la chiude con `clear`.
 *
 * Le caselle qui sono quelle **native** del modello (20 × 20,
 * `accent-color` mare), non il quadretto disegnato del briefing: il
 * browser le disegna con un bordo ben visibile in tutti e due i temi e il
 * fuoco da tastiera è il contorno rame del sito. L'avviso della presa
 * visione è il riquadro col filetto rame del modello (`.ct-err`), sotto
 * le due caselle, e la casella lo annuncia con `aria-describedby`.
 */
function Pannello({
  dati,
  presaVisione,
  onPresaVisione,
  consensoAggiornamenti,
  onConsensoAggiornamenti,
  errorePresaVisione,
  disabled,
  testa,
  className,
}: {
  dati: string;
  presaVisione: boolean;
  onPresaVisione: (v: boolean) => void;
  consensoAggiornamenti: boolean;
  onConsensoAggiornamenti: (v: boolean) => void;
  errorePresaVisione?: string;
  disabled: boolean;
  testa?: ReactNode;
  className: string;
}) {
  return (
    <fieldset
      disabled={disabled}
      className={`m-0 min-w-0 border-0 p-0 transition-opacity disabled:opacity-60 ${className}`}
    >
      <legend className={`float-left mb-3 w-full p-0 ${ETICHETTA_ACCESA}`}>
        Informativa e consensi · Modalità riservata
      </legend>
      <div className="clear-both">{testa}</div>
      <TestoInformativa
        dati={dati}
        className="m-0 mt-3 font-sans text-sommario leading-[1.55] text-sommesso"
        classeLink="font-semibold text-mare"
      />
      <SpuntaPannello
        campo="presaVisione"
        checked={presaVisione}
        onChange={onPresaVisione}
        errore={errorePresaVisione}
        nota={NOTA_PRESA_VISIONE}
      >
        {/* «Ho letto», non «acconsento»: la ragione per esteso sta in
            `lib/contatti.ts`, accanto al controllo. */}
        Ho letto l&apos;informativa privacy e so come saranno trattati i miei dati.{" "}
        <span aria-hidden>*</span>
      </SpuntaPannello>
      <SpuntaPannello
        campo="consensoAggiornamenti"
        checked={consensoAggiornamenti}
        onChange={onConsensoAggiornamenti}
        nota={NOTA_AGGIORNAMENTI}
      >
        Voglio ricevere gli aggiornamenti editoriali dell&apos;Osservatorio.
      </SpuntaPannello>
      {errorePresaVisione ? (
        <p
          id="errore-presaVisione"
          role="alert"
          className="m-0 mt-3 border-l-[3px] border-segnale bg-bg px-3.5 py-2.5 font-sans text-corpo text-ink"
        >
          {errorePresaVisione}
        </p>
      ) : null}
    </fieldset>
  );
}

/** Una casella del pannello (`.ct-spunta`): la casella nativa e, accanto,
 *  la frase Geist 15/600 con la nota 13 sonda sotto. */
function SpuntaPannello({
  campo,
  checked,
  onChange,
  errore,
  nota,
  children,
}: {
  campo: "presaVisione" | "consensoAggiornamenti";
  checked: boolean;
  onChange: (v: boolean) => void;
  errore?: string;
  nota: string;
  children: ReactNode;
}) {
  const idNota = `nota-${campo}`;
  return (
    <label className="mt-3 flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        data-campo={campo}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-invalid={errore ? true : undefined}
        aria-describedby={errore ? `${idNota} errore-${campo}` : idNota}
        className={`mt-0.5 size-5 flex-none cursor-pointer accent-mare ${FUOCO_HOME}`}
      />
      <span className="flex flex-col gap-[3px]">
        <span className="font-sans text-sommario font-semibold text-ink">{children}</span>
        <span id={idNota} className="font-sans text-voce leading-[1.5] text-sommesso">
          {nota}
        </span>
      </span>
    </label>
  );
}

/**
 * Una casella di consenso, con l'etichetta in tondo e una nota sotto.
 *
 * Il corpo è `text-corpo`, cioè la misura del testo che si legge, e non
 * quella delle etichette del form: un consenso scritto in caratteri più
 * piccoli del resto non è raccolto in modo trasparente, e la trasparenza
 * qui è un requisito, non una cortesia.
 *
 * Il quadretto è disegnato ma l'input è un `<input type="checkbox">`
 * vero, solo tolto alla vista con `sr-only`: resta nel giro del TAB, si
 * spunta con la barra spaziatrice, i lettori di schermo lo annunciano
 * come casella e il `<label>` che lo avvolge lo rende cliccabile su tutta
 * la riga. Un `<div role="checkbox">` avrebbe voluto tutto questo
 * riscritto a mano, e per un consenso non è il posto dove sperimentare.
 */
function Casella({
  campo,
  checked,
  onChange,
  errore,
  nota,
  children,
}: {
  campo: "presaVisione" | "consensoAggiornamenti";
  checked: boolean;
  onChange: (v: boolean) => void;
  errore?: string;
  nota: string;
  children: React.ReactNode;
}) {
  const idNota = `nota-${campo}`;
  const idErrore = `errore-${campo}`;

  return (
    <div>
      <label className="grid cursor-pointer grid-cols-[auto_1fr] items-start gap-3">
        <input
          type="checkbox"
          data-campo={campo}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={errore ? true : undefined}
          aria-describedby={errore ? `${idNota} ${idErrore}` : idNota}
          className="peer sr-only"
        />
        {/* Il bordo passa da una custom property e non da uno `style`
            inline: l'inline vincerebbe su `peer-checked:border-accent` e
            la casella spuntata resterebbe grigia.

            A riposo è `--color-muted` e non `--color-rule`, che è il
            filetto delle separazioni: nel tema scuro quello sta a 1,07:1
            dalla carta, cioè una casella da spuntare che non si vede, ed
            è l'unico comando del modulo senza il quale non si invia.
            `muted` sta a 6,7:1 nel chiaro e 5,4:1 nello scuro. */}
        <span
          aria-hidden
          style={
            {
              "--bordo-casella": errore ? TONO_ERRORE : "var(--color-muted)",
            } as React.CSSProperties
          }
          className="mt-[3px] grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[2px] border-2 border-[var(--bordo-casella)] bg-bg transition-colors peer-checked:border-accent peer-checked:bg-accent peer-checked:[&_svg]:opacity-100 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent"
        >
          <svg
            viewBox="0 0 12 12"
            className="h-[12px] w-[12px] text-onAccent opacity-0 transition-opacity"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M 2.5 6.3 L 5 8.8 L 9.5 3.4" />
          </svg>
        </span>
        <span className="oi-corpo text-corpo leading-[1.5] text-ink">{children}</span>
      </label>
      <p id={idNota} className="m-0 mt-1 pl-[30px] text-meta leading-[1.5] text-muted">
        {nota}
      </p>
      {errore ? (
        <MessaggioErrore id={idErrore} className="mt-1 pl-[30px]">
          {errore}
        </MessaggioErrore>
      ) : null}
    </div>
  );
}
